import React, { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Box, Button, TextField, Typography } from '@mui/material';
import { easyWebinarLogoLight } from '../../../../assets/images';
import { AI_AUTH_COLORS } from '../../auth/constants';
import { ChevronRight } from 'lucide-react';
import { toast } from 'react-toastify';
import AuthService from '../../../../services/auth.service';

export default function TryAiFlowForgotPasswordPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');

  const isSubmitDisabled = useMemo(() => email.trim().length === 0, [email]);

  const handleSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const trimmedEmail = email.trim();
      if (!trimmedEmail) return;
      try {
        const response = await AuthService.checkEmailExists(trimmedEmail);
        if (response.data.exists) {
          await AuthService.forgotPassword(trimmedEmail);
          toast.success('Reset password code sent to your email');
          navigate(
            `/try-ai-flow/auth/forgot-password/verify?email=${encodeURIComponent(trimmedEmail)}`
          );
          return;
        } else {
          toast.error('Invalid email');
          return;
        }
      } catch (error: any) {
        toast.error(error?.message || 'An error occurred. Please try again.');
      }
    },
    [email, navigate]
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        bgcolor: '#ffffff',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          py: { xs: 1.5, sm: 2 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Link to="/try-ai-flow" style={{ display: 'inline-flex', alignItems: 'center' }}>
          <img
            src={easyWebinarLogoLight}
            alt="EasyWebinar"
            style={{ display: 'block', height: 30, width: 'auto' }}
          />
        </Link>
        <Button
          component={Link}
          to="/try-ai-flow/auth/login"
          variant="contained"
          endIcon={<ChevronRight size={16} />}
          sx={{
            textTransform: 'none',
            borderRadius: '999px',
            px: 1.9,
            py: 0.7,
            fontSize: '0.85rem',
            fontWeight: 600,
            color: '#111827',
            bgcolor: '#FFFFFF',
            boxShadow: 'none',
            '&:hover': {
              bgcolor: '#F9FAFB',
              boxShadow: 'none',
            },
          }}
        >
          Login
        </Button>
      </Box>

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
          py: 4,
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 430 }}>
          <Typography
            component="h1"
            sx={{
              textAlign: 'center',
              color: '#111827',
              fontWeight: 600,
              fontSize: { xs: '2rem', sm: '2.2rem' },
              letterSpacing: '-0.01em',
              mb: 0.8,
            }}
          >
            Reset your password
          </Typography>
          <Typography
            sx={{
              textAlign: 'center',
              color: '#6B7280',
              fontSize: '0.92rem',
              mb: 2.2,
            }}
          >
            Enter your email address to reset your password
          </Typography>

          <Box component="form" onSubmit={handleSubmit} autoComplete="off">
            <TextField
              fullWidth
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="Email address"
              variant="outlined"
              size="medium"
              inputProps={{ autoComplete: 'off' }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                  bgcolor: '#FFFFFF',
                  '& fieldset': { borderColor: '#E5E7EB' },
                  '&:hover fieldset': { borderColor: '#D1D5DB' },
                  '&.Mui-focused fieldset': { borderColor: AI_AUTH_COLORS.primaryBorder },
                },
                '& input': { fontSize: '0.9rem', py: 1.1 },
                mb: 1.5,
              }}
            />

            <Button
              fullWidth
              type="submit"
              variant="contained"
              disabled={isSubmitDisabled}
              sx={{
                textTransform: 'none',
                borderRadius: '10px',
                py: 1.2,
                fontSize: '0.93rem',
                fontWeight: 600,
                color: '#FFFFFF',
                bgcolor: AI_AUTH_COLORS.primaryMain,
                boxShadow: 'none',
                '&:hover': {
                  bgcolor: AI_AUTH_COLORS.primaryHover,
                  boxShadow: 'none',
                },
                '&.Mui-disabled': {
                  bgcolor: AI_AUTH_COLORS.primaryDisabled,
                  color: '#FFFFFF',
                },
              }}
            >
              Send Reset Code
            </Button>
          </Box>

          <Box
            sx={{
              mt: 4.6,
              textAlign: 'center',
              color: '#9CA3AF',
              fontSize: '0.86rem',
            }}
          >
            <Typography sx={{ fontSize: 'inherit', color: 'inherit', mb: 0.6 }}>
              &copy; 2026 EasyWebinar. All Rights Reserved
            </Typography>
            <Box sx={{ display: 'inline-flex', gap: 2.6 }}>
              <Typography
                component="a"
                href="https://easywebinar.com/terms-conditions/"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  fontSize: 'inherit',
                  color: 'inherit',
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                Terms of Service
              </Typography>
              <Typography
                component="a"
                href="https://easywebinar.com/privacy/"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  fontSize: 'inherit',
                  color: 'inherit',
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                Privacy Policy
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
